import { RouteObject } from 'react-router-dom';
import React, { lazy, Suspense } from 'react';
import { canadianCities } from '../data/locations';
import { industries, priorityLocationIndustryCombos } from '../data/industries';

// Loading component
const LoadingPage = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading page...</p>
    </div>
  </div>
);

// Create lazy-loaded components with proper error handling
const LocationPage = lazy(() => 
  import('../pages/SEO/LocationPage').catch(() => ({
    default: () => <div>Error loading location page</div>
  }))
);

const IndustryPage = lazy(() => 
  import('../pages/SEO/IndustryPage').catch(() => ({
    default: () => <div>Error loading industry page</div>
  }))
);

const ComboPage = lazy(() => 
  import('../pages/SEO/ComboPage').catch(() => ({
    default: () => <div>Error loading combo page</div>
  }))
);

const LocationsIndex = lazy(() => 
  import('../pages/SEO/LocationsIndex').catch(() => ({
    default: () => <div>Error loading locations index</div>
  }))
);

const IndustriesIndex = lazy(() => 
  import('../pages/SEO/IndustriesIndex').catch(() => ({
    default: () => <div>Error loading industries index</div>
  }))
);

// Helper to wrap with Suspense
const withSuspense = (Component: React.ComponentType<any>, props?: any) => (
  <Suspense fallback={<LoadingPage />}>
    <Component {...props} />
  </Suspense>
);

// Generate location routes
export const locationRoutes: RouteObject[] = [
  {
    path: '/locations',
    element: withSuspense(LocationsIndex),
  },
  ...canadianCities.map(city => ({
    path: `/financial-advisor-${city.slug}`,
    element: withSuspense(LocationPage, { city }),
  }))
];

// Generate industry routes
export const industryRoutes: RouteObject[] = [
  {
    path: '/industries',
    element: withSuspense(IndustriesIndex),
  },
  ...industries.map(industry => ({
    path: `/${industry.slug}-financial-advisor`,
    element: withSuspense(IndustryPage, { industry }),
  }))
];

// Generate combination routes
export const comboRoutes: RouteObject[] = priorityLocationIndustryCombos.map(({ city, industry }) => ({
  path: `/${industry}-${city}`,
  element: withSuspense(ComboPage, { citySlug: city, industrySlug: industry }),
}));

// Regional hub routes for SK and AB
export const regionalRoutes: RouteObject[] = [
  {
    path: '/alberta-financial-advisor',
    element: withSuspense(LocationPage, { regionSlug: 'alberta' }),
  },
  {
    path: '/saskatchewan-financial-advisor',
    element: withSuspense(LocationPage, { regionSlug: 'saskatchewan' }),
  },
  {
    path: '/prairie-financial-advisor',
    element: withSuspense(LocationPage, { regionSlug: 'prairie' }),
  }
];

// Export all SEO routes
export const seoRoutesFixed: RouteObject[] = [
  ...locationRoutes,
  ...industryRoutes,
  ...comboRoutes,
  ...regionalRoutes
];